import React, { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import {
  ShareMenuIconCopy,
  ShareMenuIconDevice,
  ShareMenuIconEmail,
  ShareMenuIconFacebook,
  ShareMenuIconLinkedIn,
  ShareMenuIconReddit,
  ShareMenuIconTelegram,
  ShareMenuIconWhatsApp,
  ShareMenuIconX,
} from "../ScholarLeague/shareMenuIcons";
import "../ScholarLeague/ScholarLeague.scss";
import "./ShareMenu.scss";

const copyTextToClipboard = async (text) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

const resolveUrl = (link) => {
  const fromProp = link != null ? String(link).trim() : "";
  if (fromProp) return fromProp;
  return typeof window !== "undefined" ? window.location.href : "";
};

const isMobileDevice = () =>
  typeof navigator !== "undefined" &&
  /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 6l12 12M18 6L6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ShareMenu = ({
  shareLink,
  shareTitle = "Check this out",
  buttonLabel = "Share",
  buttonIcon,
  modalTitle = "Share",
}) => {
  const [open, setOpen] = useState(false);
  const [copyHint, setCopyHint] = useState("");

  const close = useCallback(() => {
    setOpen(false);
    setCopyHint("");
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, close]);

  const buildShareLinks = () => {
    const url = resolveUrl(shareLink);
    const encodedUrl = encodeURIComponent(url);
    const text = encodeURIComponent(`${shareTitle} — ${url}`);
    const titleEnc = encodeURIComponent(shareTitle);
    return {
      url,
      twitter: `https://twitter.com/intent/tweet?text=${titleEnc}&url=${encodedUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      whatsapp: `https://wa.me/?text=${text}`,
      telegram: `https://t.me/share/url?url=${encodedUrl}&text=${titleEnc}`,
      reddit: `https://www.reddit.com/submit?url=${encodedUrl}&title=${titleEnc}`,
      email: `mailto:?subject=${titleEnc}&body=${text}`,
    };
  };

  const openTarget = useCallback(
    (href, targetBlank = true) => {
      if (!href || href === "#") return;
      if (targetBlank && href.startsWith("http")) {
        window.open(href, "_blank", "noopener,noreferrer");
      } else {
        window.location.href = href;
      }
      close();
    },
    [close]
  );

  const handleNativeShare = useCallback(async () => {
    const url = resolveUrl(shareLink);
    if (!url || !navigator.share) return;
    try {
      await navigator.share({
        title: shareTitle,
        text: shareTitle,
        url,
      });
      close();
    } catch (err) {
      if (err?.name !== "AbortError") {
        setCopyHint("Could not open system share.");
      }
    }
  }, [shareLink, shareTitle, close]);

  const handleCopyLink = useCallback(async () => {
    const url = resolveUrl(shareLink);
    const ok = await copyTextToClipboard(url);
    setCopyHint(ok ? "Link copied" : "Copy failed");
    setTimeout(() => setCopyHint(""), 2500);
  }, [shareLink]);

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) close();
    },
    [close]
  );

  // Only show "Share via device…" on mobile (where the OS share sheet is
  // reliably populated). On desktop the OS Share contract often shows an
  // empty list, so we hide it and offer per-network options instead.
  const showNativeShare =
    typeof navigator !== "undefined" && !!navigator.share && isMobileDevice();

  const modal =
    open && typeof document !== "undefined"
      ? ReactDOM.createPortal(
          <div
            className="share-modal-overlay"
            role="presentation"
            onClick={handleOverlayClick}
          >
            <div
              className="share-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="share-modal-title"
            >
              <div className="share-modal__header">
                <h3 className="share-modal__title" id="share-modal-title">
                  {modalTitle}
                </h3>
                <button
                  type="button"
                  className="share-modal__close"
                  onClick={close}
                  aria-label="Close share menu"
                >
                  <CloseIcon />
                </button>
              </div>

              <div className="share-modal__body">
                {showNativeShare ? (
                  <button
                    type="button"
                    className="share-modal__item"
                    onClick={handleNativeShare}
                  >
                    <span className="share-modal__item-icon" aria-hidden>
                      <ShareMenuIconDevice />
                    </span>
                    <span className="share-modal__item-label">
                      Share via device…
                    </span>
                  </button>
                ) : null}

                <button
                  type="button"
                  className="share-modal__item"
                  onClick={handleCopyLink}
                >
                  <span className="share-modal__item-icon" aria-hidden>
                    <ShareMenuIconCopy />
                  </span>
                  <span className="share-modal__item-label">Copy link</span>
                </button>
                {copyHint ? (
                  <p
                    className={`share-modal__copy-hint${
                      copyHint === "Copy failed" ||
                      copyHint.startsWith("Could not")
                        ? " share-modal__copy-hint--warn"
                        : ""
                    }`}
                  >
                    {copyHint}
                  </p>
                ) : null}

                <div className="share-modal__divider" aria-hidden />

                <button
                  type="button"
                  className="share-modal__item"
                  onClick={() => openTarget(buildShareLinks().twitter)}
                >
                  <span className="share-modal__item-icon" aria-hidden>
                    <ShareMenuIconX />
                  </span>
                  <span className="share-modal__item-label">X (Twitter)</span>
                </button>
                <button
                  type="button"
                  className="share-modal__item"
                  onClick={() => openTarget(buildShareLinks().facebook)}
                >
                  <span className="share-modal__item-icon" aria-hidden>
                    <ShareMenuIconFacebook />
                  </span>
                  <span className="share-modal__item-label">Facebook</span>
                </button>
                <button
                  type="button"
                  className="share-modal__item"
                  onClick={() => openTarget(buildShareLinks().linkedin)}
                >
                  <span className="share-modal__item-icon" aria-hidden>
                    <ShareMenuIconLinkedIn />
                  </span>
                  <span className="share-modal__item-label">LinkedIn</span>
                </button>
                <button
                  type="button"
                  className="share-modal__item"
                  onClick={() => openTarget(buildShareLinks().whatsapp)}
                >
                  <span className="share-modal__item-icon" aria-hidden>
                    <ShareMenuIconWhatsApp />
                  </span>
                  <span className="share-modal__item-label">WhatsApp</span>
                </button>
                <button
                  type="button"
                  className="share-modal__item"
                  onClick={() => openTarget(buildShareLinks().telegram)}
                >
                  <span className="share-modal__item-icon" aria-hidden>
                    <ShareMenuIconTelegram />
                  </span>
                  <span className="share-modal__item-label">Telegram</span>
                </button>
                <button
                  type="button"
                  className="share-modal__item"
                  onClick={() => openTarget(buildShareLinks().reddit)}
                >
                  <span className="share-modal__item-icon" aria-hidden>
                    <ShareMenuIconReddit />
                  </span>
                  <span className="share-modal__item-label">Reddit</span>
                </button>
                <button
                  type="button"
                  className="share-modal__item"
                  onClick={() => openTarget(buildShareLinks().email, false)}
                >
                  <span className="share-modal__item-icon" aria-hidden>
                    <ShareMenuIconEmail />
                  </span>
                  <span className="share-modal__item-label">Email</span>
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className="league-share-wrap">
      <div className="league-share-anchor">
        <button
          type="button"
          className="league-share-achievements-btn"
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen(true)}
        >
          <span className="league-share-achievements-btn-label">
            {buttonLabel}
          </span>
          {buttonIcon ? (
            <img
              src={buttonIcon}
              alt=""
              className="league-share-achievements-btn-icon"
              width={20}
              height={20}
              aria-hidden
            />
          ) : null}
        </button>
      </div>
      {modal}
    </div>
  );
};

export default ShareMenu;
